import { useMetricQuery } from "./useMetricQuery";
import { parseMetricLineage } from "./parseMetricExpression";
import { camelCased } from "@/utils/misc/camelCased";
import { useMemo } from "react";
import { describeWindowOperation } from "./helpers/getWindowDescription";
import { getMetricName } from "./helpers/getMetricName";
import { useApiQuery } from "@/api/hooks";
import { getMetricsV2 } from "@/entities/Finance/Finance.queries";

export function useMetricData() {
    const { currentMetricQuery } = useMetricQuery();
    const { data, isLoading } = useApiQuery(getMetricsV2, {
        params: { metricQuery: currentMetricQuery?.metricQuery },
        key: [currentMetricQuery?.metricQuery],
        enabled: !!currentMetricQuery?.metricQuery,
    });

    const tablePeriods = useMemo(
        () =>
            (data as unknown as any)?.metrics[
                currentMetricQuery.metricQuery
            ]?.[0].lineaged_values.map((value: any) => ({
                period: value.period,
                value: value.value,
            })),
        [data, currentMetricQuery.metricQuery],
    );
    const parsed = useMemo(
        () => (data ? parseMetricLineage(camelCased(data)) : undefined),
        [data],
    );
    const description = useMemo(() => {
        const fullDescription = (data as unknown as any)?.metrics[
            currentMetricQuery.metricQuery
        ]?.[0]?.description?.description;
        if (!fullDescription) return undefined;
        const parts = fullDescription.split("\n\n");
        const out = parts.length >= 3 ? parts[1] : fullDescription;
        return out;
    }, [data, currentMetricQuery.metricQuery]);

    const windowDescription = useMemo(
        () => describeWindowOperation(currentMetricQuery),
        [currentMetricQuery],
    );

    const name = useMemo(
        () => getMetricName(currentMetricQuery),
        [currentMetricQuery],
    );

    const formula = useMemo(() => {
        const fullDescription = (data as unknown as any)?.metrics[
            currentMetricQuery.metricQuery
        ]?.[0]?.description.description;

        if (!fullDescription) return [];

        const afterExpression = fullDescription.split(
            "With the expression:\n",
        )[1];
        if (!afterExpression) return [];

        const codeBlockMatch = afterExpression.match(/```text\n([\s\S]*?)```/);
        if (!codeBlockMatch) return [];

        const formulaString = codeBlockMatch[1];
        const functionMatch = formulaString.match(/(\w+)\s*\(([\s\S]*)\)/);

        if (!functionMatch) {
            return [];
        }

        const [, , innerContent] = functionMatch;

        return innerContent
            .split(",")
            .map((operand) => operand.trim())
            .filter((operand) => operand && operand !== "");
    }, [data, currentMetricQuery.metricQuery]);

    return {
        parsed,
        tablePeriods,
        description,
        name,
        windowDescription,
        formula,
        isLoading,
    };
}
